import React, { useEffect, useMemo, useState } from "react";
import { confirmDialog } from "../../utils/toast";

// 슈퍼어드민 전용 — 지점별 팀 관리.
// 지점 관리(BranchSettings)와 같은 구조로 맞췄다: [지점 목록(검색)] → 지점 클릭 시 [팀 팝업]에서 추가/삭제.
//
// 팀은 <회사 + 지점> 아래 단위라 지점이 먼저 등록돼 있어야 한다.
// 등록되지 않은 지점에 팀을 만들면 가입 화면에 영영 안 뜨므로 서버가 막는다.
export default function TeamSettings() {
  const [teams, setTeams] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState(null); // "회사|지점"
  const [addTeamName, setAddTeamName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      // 지점 목록도 같이 받는다 — 팀이 하나도 없는 지점을 보여줘야
      // "여기에 팀을 만들면 되겠구나"를 알 수 있다.
      const [teamRes, branchRes] = await Promise.all([
        fetch("/api/admin/teams", { credentials: "include" }),
        fetch("/api/admin/branches", { credentials: "include" }),
      ]);

      const teamData = await teamRes.json();
      const branchData = await branchRes.json();

      if (!teamRes.ok || !teamData?.success) {
        throw new Error(teamData?.message || teamData?.error || "팀 목록을 불러오지 못했습니다.");
      }

      setTeams(Array.isArray(teamData.teams) ? teamData.teams : []);
      setBranches(
        branchRes.ok && branchData?.success && Array.isArray(branchData.branches)
          ? branchData.branches
          : []
      );
    } catch (err) {
      setError(err.message || "팀 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // 지점 단위로 묶는다: [{ key, company, branch, teams[] }]
  const groups = useMemo(() => {
    const map = new Map();

    const put = (company, branch) => {
      const key = `${company}|${branch}`;
      if (!map.has(key)) map.set(key, { key, company, branch, teams: [] });
      return map.get(key);
    };

    branches.forEach((b) => put(b.companyName, b.branchName));
    teams.forEach((t) => put(t.companyName, t.branchName).teams.push(t));

    return Array.from(map.values()).sort(
      (a, b) =>
        a.company.localeCompare(b.company, "ko") || a.branch.localeCompare(b.branch, "ko")
    );
  }, [teams, branches]);

  const filteredGroups = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.company.toLowerCase().includes(q) ||
        g.branch.toLowerCase().includes(q) ||
        g.teams.some((t) => t.teamName.toLowerCase().includes(q))
    );
  }, [groups, search]);

  const selected = useMemo(
    () => groups.find((g) => g.key === selectedKey) || null,
    [groups, selectedKey]
  );

  const handleAddTeam = async () => {
    const name = String(addTeamName || "").trim();
    if (!selected || !name) {
      alert("팀명을 입력해주세요.");
      return;
    }

    try {
      setSaving(true);
      const res = await fetch("/api/admin/team", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          companyName: selected.company,
          branchName: selected.branch,
          teamName: name,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "팀 등록 실패");
        return;
      }
      await load();
      setAddTeamName("");
    } catch {
      alert("팀 등록 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (t) => {
    // 소속 인원이 있으면 서버가 막지만, 누르기 전에 알려주는 편이 낫다.
    const extra =
      t.userCount > 0
        ? `\n\n※ 이 팀 소속 계정이 ${t.userCount}명 있어 삭제되지 않습니다. 먼저 그 계정들의 팀을 옮겨주세요.`
        : "";

    if (
      !(await confirmDialog(`[${t.branchName} · ${t.teamName}] 팀을 삭제하시겠습니까?${extra}`, {
        title: "팀 삭제",
        danger: true,
        confirmText: "삭제",
      }))
    ) {
      return;
    }

    try {
      const res = await fetch("/api/admin/team/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ teamIdx: t.teamIdx }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "팀 삭제 실패");
        return;
      }
      await load();
    } catch {
      alert("팀 삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-[#111827]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">팀 관리</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            지점별 팀을 등록합니다. 여기 등록된 팀만 가입 화면과 계정 수정에서 고를 수 있습니다.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-white/5"
        >
          새로고침
        </button>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="회사 · 지점 · 팀 검색"
        className="mt-3 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
      />

      {error ? (
        <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="mt-3 max-h-[320px] space-y-2 overflow-y-auto pr-1">
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-400">불러오는 중...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">
            등록된 지점이 없습니다. 지점 관리에서 지점을 먼저 등록해주세요.
          </div>
        ) : (
          filteredGroups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => {
                setSelectedKey(g.key);
                setAddTeamName("");
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-left transition-colors hover:border-blue-400 dark:border-gray-700"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {g.company} · {g.branch}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
                    {g.teams.length > 0
                      ? g.teams.map((t) => t.teamName).join(" · ")
                      : "등록된 팀 없음"}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                  {g.teams.length}팀
                </span>
              </div>
            </button>
          ))
        )}
      </div>

      {selected ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-[#111827]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-base font-bold text-gray-900 dark:text-white">
                  {selected.company} · {selected.branch}
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  이 지점의 팀을 추가하거나 삭제합니다.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedKey(null)}
                className="shrink-0 rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
              >
                닫기
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {selected.teams.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-4 text-center text-xs text-gray-400 dark:border-gray-600">
                  등록된 팀이 없습니다.
                </div>
              ) : (
                selected.teams.map((t) => (
                  <div
                    key={t.teamIdx}
                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                        {t.teamName}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                        소속 {t.userCount}명
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteTeam(t)}
                      className="shrink-0 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-500/10"
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={addTeamName}
                onChange={(e) => setAddTeamName(e.target.value)}
                maxLength={100}
                placeholder="추가할 팀명"
                className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
              />
              <button
                type="button"
                onClick={handleAddTeam}
                disabled={saving}
                className="shrink-0 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "추가 중..." : "추가"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
